import React, { useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import BarChartIcon from "@material-ui/icons/BarChart";
import DeleteIcon from "@material-ui/icons/Delete";
import PieChartIcon from "@material-ui/icons/PieChart";
import * as Dati from "src/Dati";
import * as Glob from "src/Globale";
import { Trad } from "src/components";
import { _Autocomplete, _NumberField, _DateField, _Select } from "../../form";
import { AlertDialogContext } from "../../providers/AlertDialogProvider";

interface ICompaniesGroupDetailsProps {
    Data: Glob.DatiJSON;
    OnChange: (val: string) => void;
    ParentCompanyColumn?: boolean;
}

const useStyles = makeStyles(({ shape, spacing, palette, typography }: Theme) => ({
    icon: {
        marginRight: spacing(2)
    },
    title: {
        flexGrow: 1,
    },
    toolbar: {
        background: palette.warning.light,
        borderRadius: shape.borderRadius,
        color: palette.warning.contrastText,
        marginTop: spacing(4),
        marginBottom: spacing(2)
    },
    removeBtn: {
        color: palette.error.main,
        background: "none"
    },
    divider: {
        marginTop: spacing(3),
        marginBottom: spacing(3)
    }
}));

export const _CompaniesGroupDetails: React.FC<ICompaniesGroupDetailsProps> = ({ Data, OnChange, ParentCompanyColumn }) => {
    const classes = useStyles();
    const { ShowDialog } = useContext(AlertDialogContext);

    const details: Dati.CDatoMandante[] = Data ? JSON.parse(Data) : [];
    const isParentCompanySet: boolean = !!details.filter(detail => detail.Capogruppo).length;
    const outstandingShare: number = 100 - details.reduce((total, detail) => total + detail.Quota, 0);

    const OnDetailChange = (name: string, val: number | string | boolean, index: number) => {
        details[index][name] = val;
        OnChange(JSON.stringify(details));
    };

    const OnRemoveDetail = (index: number) => {
        details.splice(index, 1);
        OnChange(JSON.stringify(details));
    };

    const OnAddDetail = () => {
        const addedDetail = new Dati.CDatoMandante();

        if (details.length == 0) {
            addedDetail.Quota = 50;
        } else if (!ParentCompanyColumn) {
            addedDetail.Quota = Math.trunc(details[details.length - 1].Quota * 100) / 100;
        }
        details.push(addedDetail);
        OnChange(JSON.stringify(details));
    };

    const OnShareChange = (e: string, value: string, index: number) => {
        const val = Number(value);
        const newOutstandingShare = outstandingShare + details[index].Quota - val;

        if (ParentCompanyColumn && newOutstandingShare < 0) {
            ShowDialog("WARNING", Trad.TradParam("Impossibile impostare la quota di {1-P} perché supererebbe la soglia del 100% totale", [val]), "warning");
        } else if (!ParentCompanyColumn && newOutstandingShare < 0) {
            ShowDialog("WARNING", Trad.TradParam("Impossibile impostare la quota di {1-P} perché supererebbe la quota della mandataria", [val]), "warning");
        } else {
            OnDetailChange(e, val, index);
        }
    };

    const ValidateVatNumber = (index: number) => {
        if (!Glob.ControllaPIVA(details[index].PartitaIVA)) {
            ShowDialog("WARNING", Trad.Trad("La Partita iva non è valida"), "warning");
        }
    };

    return (
        <>
            <Toolbar variant="dense" className={classes.toolbar}>
                <PieChartIcon className={classes.icon} />
                <Typography variant="subtitle1" color="inherit" className={classes.title}>
                    {Trad.Trad("Mandanti")}
                </Typography>
                <BarChartIcon fontSize="small" />
                <Box mr={1}>
                    <Typography variant="body2" color="inherit">
                        {`${Trad.Trad(ParentCompanyColumn ? "Quota rimanente" : "Quota mandataria")}: ${Trad.TradParam("{1-P}", [outstandingShare])}`}
                    </Typography>
                </Box>
                <Tooltip title={Trad.Trad("Nuovo mandante")}>
                    <IconButton size="small" aria-label="add contact details" onClick={OnAddDetail}>
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Toolbar>
            {details.map((detail: Dati.CDatoMandante, index: number) => (
                <div key={index}>
                    <Grid container spacing={4} justify="space-between" alignItems="center" key={index}>
                        <Grid item container xs={12} md={11} spacing={4}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    value={detail.RagioneSociale}
                                    id="RagioneSociale"
                                    label={Trad.Trad("Ragione Sociale")}
                                    onChange={e => OnDetailChange(e.currentTarget.id, e.currentTarget.value, index)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <TextField
                                    value={detail.PartitaIVA}
                                    id="PartitaIVA"
                                    label={Trad.Trad("PIVA")}
                                    onChange={e => OnDetailChange(e.currentTarget.id, e.currentTarget.value, index)}
                                    onBlur={e => {
                                        ValidateVatNumber(index);
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <_NumberField Id="Quota" Label="Quota" Value={detail.Quota} Adornment="%" OnChange={(e, val) => OnShareChange(e, val, index)} />
                            </Grid>
                            {ParentCompanyColumn && (
                                <Grid item xs={12} md={3}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={detail.Capogruppo}
                                                disabled={!detail.Capogruppo && isParentCompanySet}
                                                onChange={e => OnDetailChange(e.currentTarget.name, e.currentTarget.checked, index)}
                                                name="Capogruppo"
                                            />
                                        }
                                        label={Trad.Trad("Capogruppo")}
                                    />
                                </Grid>
                            )}
                        </Grid>
                        <Grid item>
                            <Tooltip title={Trad.Trad("Elimina")}>
                                <IconButton
                                    disableRipple
                                    className={classes.removeBtn}
                                    aria-label="remove contact details"
                                    onClick={() => {
                                        OnRemoveDetail(index);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    {index < details.length - 1 && <Divider light className={classes.divider} />}
                </div>
            ))}
        </>
    );
};

_CompaniesGroupDetails.defaultProps = {
    ParentCompanyColumn: true
};



// WEBPACK FOOTER //
// ./src/inar2/components/ClientDetails/CompaniesGroupDetails.tsx